import { differenceInDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { TIMELINE_ROW_AXIS_LABEL_WIDTH } from './layout/TimelineRowAxisLabel';

export const DEFAULT_ROW_HEADER_WIDTH = 120;
export const VERTICAL_ROW_HEADER_WIDTH = 32;
export const PARENT_HEADER_HEIGHT = 32;
export const TIMELINE_HEADER_HEIGHT = 70;

export const RESIZE_HANDLE_WIDTH = 4;
export const RESIZE_HANDLE_HITBOX_PADDING = 8;
export const RESIZE_HANDLE_CENTER_OFFSET = 2;

export const useTimelineColumnDayWidth = ({
  scrollContainerRef,
  hasVerticalSubHeaders,
  hasRowAxisLabel = true,
  from,
  to,
  rowHeaderWidth = DEFAULT_ROW_HEADER_WIDTH,
  isExportMode = false,
  minDayWidth,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  hasVerticalSubHeaders: boolean;
  hasRowAxisLabel?: boolean;
  from: string;
  to: string;
  rowHeaderWidth?: number;
  isExportMode?: boolean;
  minDayWidth?: number;
}) => {
  const [containerWidth, setContainerWidth] = useState(() =>
    isExportMode ? window.innerWidth : 0,
  );

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.target.clientWidth);
      }
    });

    resizeObserver.observe(element);
    setContainerWidth(element.clientWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef]);

  return useMemo(() => {
    const axisLabelWidth = hasRowAxisLabel ? TIMELINE_ROW_AXIS_LABEL_WIDTH : 0;
    const timelineWidth = hasVerticalSubHeaders
      ? containerWidth - rowHeaderWidth - PARENT_HEADER_HEIGHT - axisLabelWidth
      : containerWidth - rowHeaderWidth - axisLabelWidth;
    // The scrollbar-avoidance buffer is only needed when content is forced to fit (no minDayWidth).
    // When minDayWidth is set the caller already allows horizontal scrolling.
    const scrollbarBuffer = minDayWidth !== undefined ? 0 : 10;
    const bufferedWidth = timelineWidth - scrollbarBuffer;
    const totalDays = differenceInDays(new Date(to), new Date(from)) + 1;
    const fittedWidth =
      totalDays <= 31 ? Math.floor(bufferedWidth / totalDays) : bufferedWidth / totalDays;

    if (minDayWidth !== undefined) {
      return Math.max(fittedWidth, minDayWidth);
    }
    return fittedWidth;
  }, [
    containerWidth,
    from,
    to,
    hasVerticalSubHeaders,
    hasRowAxisLabel,
    rowHeaderWidth,
    minDayWidth,
  ]);
};
